import { Answers } from '@care-portals/api-interfaces';
import type { FormType } from './form-types';
import { ConsultationUtils } from './consult-utils';

export type LegacyFormTypeKeys =
  | 'facesheet'
  | 'ids'
  | 'govId'
  | 'selfie'
  | 'bodySelfie'
  | 'govId_archived'
  | 'selfie_archived'
  | 'bodySelfie_archived'
  | 'drugCard'
  | 'drugCard1'
  | 'drugCard2'
  | 'drugCard3'
  | 'rxrefill'
  | 'jaxx'
  | 'hl'
  | 'hljill'
  | 'phl'
  | 'el'
  | 'bc'
  | 'sk'
  | 'sc'
  | 'transferrx'
  | 'bloodWork'
  | 'labres'
  | 'trt'
  | 'qadam'
  | 'hc'
  | 'facesheet_ext'
  | 'eczema'
  | 'st'
  | 'hyperpig'
  | 'scar'
  | 'std'
  | 'general'
  | 'final'
  | 'planb'
  | 'wlInitialMeet'
  | 'wlMealPlanning'
  | 'wlCheckIn'
  | 'checkInVisit'
  | 'uti'
  | 'wlProgress'
  | 'wlWeekly'
  | 'wlMonthlyFollowup'
  | 'wl3months'
  | 'wl6months'
  | 'wl12months'
  | 'hrt'
  | 'hrt1MonthFollowup'
  | 'hrt3MonthsFollowup'
  | 'allergies'
  | 'wlPhotos'
  | 'tripleHair'
  | 'refill'
  | 'norefill'
  | 'wl1stSession'
  | 'wlPhysical'
  | 'WLPreSW'
  | 'WLPreNP'
  | 'wlPostLabCheck'
  | 'psycho'
  | 'psychoFollowUp'
  | 'CS_VC_coordinatorsConsent'
  | 'virtualCareConcerns'
  | 'sharingInfoConsent'
  | 'epds'
  | 'phq9'
  | 'gad7'
  | 'bh-intake'
  | 'bh-partner-intake'
  | 'dass21'
  | 'pcl5'
  | 'bms_before'
  | 'bms_after'
  | 'epipen'
  | 'gn_welcome'
  | 'gn_intake'
  | 'endor_intake'
  | 'gn_post_session'
  | 'gn_blood_results'
  | 'consent'
  | 'kinesiologist_checkin'
  | 'rda25'
  | 'rda50'
  | 'mha'
  | 'rka25'
  | 'rka50'
  | 'momwell_cali_consents'
  | 'momwell_super_consents'
  | 'momwell_ny_consents'
  | 'happyasamother_co_consents'
  | 'momwell_super_consents_v2'
  | 'momwell_ny_consents_v2'
  | 'momwell_cali_consents_v2'
  | 'happyasamother_co_consents_v2'
  | 'momwell_history_form'
  | 'momwell_partner_history_form'
  | 'momwell_intake_network'
  | 'momwell_intake_cash'
  | 'telehealth_consent'
  | 'menopauseConsent'
  | 'pocketpillsWaiver'
  | 'ssrs'
  | 'momwell_super_consent_to_disclose'
  | 'momwell_ny_consent_to_disclose'
  | 'momwell_cali_consent_to_disclose'
  | 'happyasamother_co_consent_to_disclose'
  | 'enovone_testosterone_intake';

export const legacyForms: Record<LegacyFormTypeKeys, FormType> = {
  facesheet: {
    name: 'FaceSheet',
    questions: [
      'conditions',
      'surgeries',
      'medications',
      'supplements',
      'allergies'
    ]
  },
  ids: {
    name: 'IDs',
    questions: ['govId', 'selfie', 'drugCard'],
    onSubmit: (answers: Answers) => {
      ConsultationUtils.answers = answers;
      if (answers.drugCard0 !== undefined) {
        var origin = window.location.origin;
        var newUrl = origin + '/consultation/start?types=drugCard1';
        window.location.href = newUrl;
      }
    }
  },
  govId: {
    olderVersions: ['govId_archived'],
    name: 'Gov ID',
    questions: ['govIdUs']
  },
  selfie: {
    olderVersions: ['selfie_archived'],
    name: 'Selfie',
    questions: ['selfieUs']
  },
  bodySelfie: {
    olderVersions: ['bodySelfie_archived'],
    name: 'Full Body Selfie',
    questions: ['bodySelfieUs']
  },
  govId_archived: { name: 'Gov ID', questions: ['govId'] },
  selfie_archived: { name: 'Selfie', questions: ['selfie'] },
  bodySelfie_archived: {
    name: 'Full Body Selfie',
    questions: ['bodySelfie']
  },
  drugCard: {
    name: 'Drug Card',
    questions: [
      {
        key: 'drugCardExists',
        next: {
          condition: { 'drugCardExists.yes': true },
          key: 'drugCardPlan',
          els: 'drugCardComplete'
        }
      },
      'drugCardPlan',
      'drugCardInfo',
      'drugCardOwner',
      'drugCardName',
      {
        key: 'drugCardAnother',
        next: {
          condition: { 'drugCardAnother.yes': true },
          key: 'drugCardPlan2',
          els: 'drugCardComplete'
        }
      },
      'drugCardPlan2',
      'drugCardInfo2',
      'drugCardOwner2',
      'drugCardName2',
      {
        key: 'drugCardAnother2',
        next: {
          condition: { 'drugCardAnother2.yes': true },
          key: 'drugCardPlan3',
          els: 'drugCardComplete'
        }
      },
      'drugCardPlan3',
      'drugCardInfo3',
      'drugCardOwner3',
      'drugCardName3',
      {
        key: 'drugCardAnother3',
        next: {
          condition: { 'drugCardAnother3.yes': true },
          key: 'drugCardPlan4',
          els: 'drugCardComplete'
        }
      },
      'drugCardPlan4',
      'drugCardInfo4',
      'drugCardOwner4',
      'drugCardName4',
      {
        key: 'drugCardAnother4',
        next: {
          condition: { 'drugCardAnother4.yes': true },
          key: 'drugCardPlan5',
          els: 'drugCardComplete'
        }
      },
      'drugCardPlan5',
      'drugCardInfo5',
      'drugCardOwner5',
      'drugCardName5',
      'drugCardComplete'
    ]
  },
  drugCard1: {
    name: 'Drug Card 1',

    questions: [
      'drugCardPlan',
      'drugCardInfo',
      'drugCardOwner',
      'drugCardName',
      'drugCardAnother'
    ],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.drugCardAnother.yes == true) {
        var origin = window.location.origin;
        var newUrl = origin + '/consultation/start?types=drugCard2';
        window.location.href = newUrl;
      }
    }
  },
  drugCard2: {
    name: 'Drug Card-2',

    questions: [
      'drugCardPlan',
      'drugCardInfo',
      'drugCardOwner',
      'drugCardName',
      'drugCardAnother'
    ],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.drugCardAnother.yes == true) {
        var origin = window.location.origin;
        var newUrl = origin + '/consultation/start?types=drugCard3';
        window.location.href = newUrl;
      }
    }
  },
  drugCard3: {
    name: 'Drug Card-3',

    questions: [
      'drugCardPlan',
      'drugCardInfo',
      'drugCardOwner',
      'drugCardName',
      'drugCardAnother'
    ]
  },
  rxrefill: {
    name: 'Rx Refill',

    questions: ['rxInfo']
  },

  // Jack FORMS Questions

  jaxx: {
    name: 'Jaxx follow up',
    questions: ['jaxx1', 'jaxx2', 'jaxx3', 'jaxx4', 'jaxx5', 'jaxx6']
  },

  hl: {
    name: 'Hair Loss',
    questions: [
      'hairPhotos',
      'hair1',
      'hair2',
      'hair3',
      'hair4',
      'hair5',
      'hair6',
      'hair7',
      'hair8',
      'hairAcknowledgement',
      'hairAcknowledgement2'
    ]
  },
  hljill: {
    name: 'Hair Loss',
    questions: [
      'hairpregnant',
      'hairPhotos',
      'hair1',
      'hair2',
      'hair3',
      'hair4',
      'hair5',
      //'hair6',
      'hair7',
      'hair8',
      'hairAcknowledgement3',
      'hairAcknowledgement',
      'hairAcknowledgement2'
    ]
  },
  phl: {
    name: 'Promo Hair Loss',
    questions: [
      'hairPhotos',
      'hair1',
      'hair2',
      'hair3',
      'hair4',
      'hair5',
      'hair6',
      'hair7',
      'hair8',
      'phlPref'
    ]
  },

  // Jill Questions
  el: {
    name: 'Eyelashes',
    questions: [
      'whyBenefit',
      'symptoms',
      'thickenedOrWeakPoints',
      'eyeIssuesHistory',
      'eyedropSensitivity',
      'ingredientsSensitivity',
      'isPregnant',
      'currentlyBreastfeeding',
      'consent1',
      'consent2',
      'consent3'
    ]
  },
  // BIRTH CONTROL
  bc: {
    name: 'Birth Control',
    questions: [
      'birthControl', //1
      'everUsed', //2
      'headache', //3
      'menstrual', //4
      'pregnant', //5
      'givenBirth', //6
      'breastfeeding', //7
      'height', //8
      'weight', //9
      'periods', //11
      'pelvicexam', //12
      'breastexam', //13
      'bloodClots', //16
      'cigarettes', //17
      'medicalTeam' //18
    ]
  },

  //SKIN CARE
  sk: {
    name: 'Skin',
    questions: [
      'diagnose', //1
      'counterTreatment', //2
      'bestSkinType', //3
      'sunscreen', //4
      'affectedParts', //5
      'pregnantFuture', //6
      'skinReaction', //7
      'diagnosed', //8
      'elseAcne', //9
      'skinAcknowledgement'
    ]
  },
  sc: {
    name: 'Smoking Cessation',
    questions: [
      'scAge',
      'scGender',
      'scCigarettes',
      'scWeekendCigs',
      'firstCigarette',
      'typeSmoking',
      'ageSmoking',
      'scMaxPerDay',
      'scQuitting',
      'scLifeStyle',
      'scMedicalCond',
      'scMedication'
    ]
  },

  transferrx: {
    name: 'RX Transfer',
    questions: ['transferRx', 'transferRX2', 'transferRX3']
  },

  bloodWork: {
    name: 'Blood Work check',
    questions: ['didBloodWork'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.didBloodWork.haveResult == true) {
        var origin = window.location.origin;
        var newUrl = origin + '/consultation/start?types=labres';
        window.location.href = newUrl;
      }
    }
  },

  labres: {
    name: 'Lab Test Results',
    questions: ['labResultUpload']
  },

  trt: {
    name: 'Testosterone Replacement Therapy',
    questions: [
      'trtSymptoms',
      'trtConditions',
      'trtPhysicalExam',
      'trtBp',
      'trtConsume',
      'trtStress',
      'trtFamilyHistory',
      'trtElse',
      'trtBmi',
      'trtSyringe',
      'trtWeightIssue',
      'trtHealthBenefits',
      'trtMoveBody',
      'trtSleep',
      'trtDiet',
      'trtStarch',
      'trtAcknowledgement'
    ]
    // onSubmit: (answers: Answers) => {
    // ConsultationUtils.answers = answers;
    // window.location.href = "https://calendly.com/bianca-cordeiro-rd/intro-call";
    // window.location.href = 'https://calendly.com/catrina-jackhealth/30min';
    // }
  },

  qadam: {
    name: 'QADAM',
    questions: [
      'qadamLibido',
      'qadamEnerge',
      'qadamStrength',
      'qadamEnjoyment',
      'qadamHappiness',
      'qadamErections',
      'qadamPerformance',
      'qadamAsleep',
      'qadamSports',
      'qadamHeight',
      'qadamEvaluated'
    ]
  },
  hc: {
    name: 'Health Card',
    questions: ['hcProvince', 'hcCardInfo']
  },
  facesheet_ext: {
    name: 'Health Details',
    questions: [
      'kidneysNotWorking',
      'liverNotWorking',
      'heartNotPumping',
      'havePrimaryProvider',
      'hadCheckoutIn3Years'
    ]
  },

  eczema: {
    name: 'Eczema',
    questions: [
      'eczAffected',
      'eczDiagnosed',
      'eczMedical',
      'eczDescSkin',
      'eczSPF',
      'eczBodyEffected',
      'eczPregnant',
      'eczReaction',
      'eczDiagnosedAny',
      'eczGoalsSkin',
      'eczApplySkin',
      'eczValuable',
      'eczAnythingElse'
    ]
  },

  st: {
    name: 'Skin Tags',
    questions: [
      'stagDiagnosed',
      'stagLocated',
      'stagLong',
      'stagLesion',
      'stagUploadPicture'
    ]
  },
  hyperpig: {
    name: 'Hyperpigmentation',
    questions: [
      'hyperpigLocation',
      'pregnantFuture',
      'hyperpigUploadPicture',
      'hyperpigExtra'
    ]
  },

  scar: {
    name: 'Scarring',
    questions: [
      'scarImages',
      'scarCause',
      'scarBody',
      'scarDescSkin',
      'scarPrescription',
      'scarOverCounter',
      'scarSPF',
      'scarWear',
      'scarPregnant'
    ]
  },

  std: {
    name: 'STD Test',
    questions: ['stdAge', 'stdGender', 'stdExperience', 'stdUnprotected']
  },

  general: {
    name: 'General Acne',
    questions: ['genGender', 'genPronoun', 'genRace']
  },

  final: {
    name: 'Final Acne Questionnaire',
    questions: [
      [
        {
          key: 'finalPhotos',
          next: {
            condition: { 'finalPhotos.photoYes': true },
            key: 'finalUpload',
            els: 'finalTries'
          }
        },
        'finalUpload',
        'finalUploadDone'
      ],
      [
        {
          key: 'finalTries',
          next: {
            condition: { 'finalTries.retinol': true },
            key: 'finalTriesMore',
            els: 'finalDiagnosed'
          }
        },
        'finalTriesMore'
      ],
      [
        {
          key: 'finalDiagnosed',
          next: {
            condition: { 'finalDiagnosed.noDiagnosed': { $ne: true } },
            key: 'finalPrescription',
            els: 'finalOral'
          }
        },
        {
          key: 'finalPrescription',
          next: {
            condition: {
              'finalPrescription.retinA': { $ne: true },
              'finalPrescription.prescriptionNone': { $ne: true }
            },
            key: 'finalPrescriptionMore',
            els: 'finalOral'
          }
        },
        'finalPrescriptionMore'
      ],
      [
        {
          key: 'finalOral',
          next: {
            condition: { 'finalOral.oralYes': true },
            key: 'finalOralDetail',
            els: 'finalComplete'
          }
        },
        'finalOralDetail',
        'finalOralMore'
      ],
      'finalComplete'
    ]
  },

  planb: {
    name: 'PlanB',
    questions: [
      'planBSexual',
      'planBMenstrual',
      'planBIrregular',
      'planBBC',
      'planBBmi',
      'planBTablets',
      'planBMissTablet',
      'planBContraception',
      'planBAcknowledgement'
    ]
  },

  wlInitialMeet: {
    name: 'Weight Loss Initial Meet',
    questions: [
      'imFitsDefi',
      'imHighestProtein',
      'imUsuallyMeal',
      'imUsuallyDrink',
      'imUsuallyOpt',
      'imMealSnackHighProtein',
      'imMealSnackIncludeVeggiesFruit',
      'imEducationSlide'
    ]
  },

  wlMealPlanning: {
    name: 'Weight Loss Meal Planning',
    questions: [
      'mpEducationSlide1',
      'mpHighProteinFoodsYouLike',
      'mpVegetablesFruitYouLike',
      'mpHighFiberGrains',
      'mpFoodsYouDislike',
      'mpEducationSlide2'
    ]
  },

  wlCheckIn: {
    name: 'Weight Loss Check In',
    questions: [
      'ciDietitianConsultation',
      'ciBiggestWin',
      'ciSkipBreakfast',
      'ciSkipLunch',
      'ciSkipDinner',
      'cisnackOften',
      'ciBiggestChallenge',
      'ciBiggestFactor',
      'ciEducationSlide'
    ]
  },

  checkInVisit: {
    name: 'Check-In Visit',
    questions: [
      'civTreatment',
      'civSideEffect',
      'civWorking',
      'civUpdateTreatment',
      'civEducationl1'
    ],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.civUpdateTreatment.yesChangeTreatment == true) {
        window.location.href = ConsultationUtils.getMessagesUrl();
      }
    }
  },

  uti: {
    name: 'UTI',
    questions: [
      'utiApplyToYou',
      'utiLastUTI',
      'utiExperiecSymptomsFirst',
      'utiExperiecSymptomsSecond',
      'utiAllergicMedications',
      'utiEduacationalSlide'
    ]
  },

  wlProgress: {
    name: 'Weight Loss Monthly',
    questions: ['wlProgress']
  },

  wlWeekly: {
    name: 'Weight loss weekly update',
    questions: ['wlWeekly']
  },

  wlMonthlyFollowup: {
    name: 'Weight Loss Monthly follow up',
    questions: [
      {
        key: 'wlMonthlyWeightChange',
        next: {
          condition: { 'wlMonthlyWeightChange.notTracking': true },
          key: 'wlMonthlyWeightTrackingInfo',
          els: 'wlMonthlyProgressConfidence'
        }
      },
      'wlMonthlyWeightTrackingInfo',
      'wlMonthlyProgressConfidence',
      'wlMonthlySideEffects',
      'wlMonthlyAdherence',
      'wlMonthlyAppetiteControl',
      'wlMonthlyIntentSignal',
      'wlMonthlyProvince'
    ]
  },

  wl3months: {
    name: 'Weight loss 3 months follow up',
    questions: [
      'wl3months',
      'wl3monthsSummery',
      'wl3monthsWeightNow',
      'wl3monthsWeightLossFromStart',
      'wlFUSymptoms3M',
      'wl3monthsConcerns',
      'wl3monthsWeightLossSoFar',
      'wl3monthsNameAndDoseOfMedication',
      'wlFU3Habbits',
      'wLFU3Diet',
      'wl3monthsConsentForAnother3Months'
    ]
  },

  wl6months: {
    name: 'Weight loss 6 months follow up',
    questions: [
      'wl6months',
      'wl6monthsWeightNow',
      'wl6monthsWeightLoss',
      'wl6monthsIsBowelMovementsIssue',
      'wlFUSymptoms3M',
      'wlFU3Habbits',
      'wLFU3Diet',
      'wl6monthsEatingHabits',
      //'wl6monthsIsEatingConsistentlyAndDrinkingFluids',
      'wl6monthsSupplments',
      'wl6monthsIsMedicalHealthChange'
    ]
  },

  wl12months: {
    name: 'Weight loss 12 months follow up',
    questions: [
      'wl12months',
      'wl12monthsWeightNow',
      'wl12monthsWeightLoss',
      'wl12monthsLookingTo',
      'wl12monthsIsEatingConsistentlyAndDrinkingFluids',
      'wl12monthsIsMedicalHealthChange',
      'wl12monthsIssues',
      'wlFUSymptoms3M',
      'wlFU3Habbits',
      'wLFU3Diet',
      'wl6monthsEatingHabits',
      'wl6monthsSupplments',
      // TODO file upload not working
      // 'wl12monthsNutritionTrackingApp',
      'wl12monthsDayDietSample'
    ]
  },

  hrt: {
    name: 'hrt',
    questions: [
      'hrtBMI',
      [
        {
          key: 'hrtPeriods',
          next: {
            condition: { 'hrtPeriods.yes': true },
            key: 'hrtPeriodChanges',
            els: 'hrtexp1'
          }
        },
        'hrtPeriodChanges'
      ],
      'hrtexp1',
      'hrtexp2',
      'hrtbladder',
      'hrtmood',
      'hrtconditions',
      'hrtconditions2',
      'hrtHyster',
      'hrtMestrual',
      'hrtbc',
      'hrttherapy',
      'hrtanythingelse'
    ]
  },

  hrt1MonthFollowup: {
    name: 'HRT 1 Month Followup',
    questions: [
      'hrt1MonthFollowup_pleaseDescribeHowYouVeBeenTakingYourMedication',
      'hrt1MonthFollowup_doYouForgetToTakeYourMedication',
      'hrt1MonthFollowup_areYouHavingHotFlashes',
      'hrt1MonthFollowup_areYouExperiencingAnyVaginalSymptoms',
      'hrt1MonthFollowup_howIsYourMood',
      'hrt1MonthFollowup_howIsYourSleep',
      'hrt1MonthFollowup_hrtPeriodChanges',
      'hrt1MonthFollowup_haveYouStartedAnyNewMedications',
      'hrt1MonthFollowup_areThereAnyOtherSymptomsYouAreExperiencing'
    ]
  },

  hrt3MonthsFollowup: {
    name: 'HRT 3 Months Followup',
    questions: [
      'hrt3MonthsFollowup_Intro',
      {
        key: 'hrt3MonthsFollowup_ifYouAreOnProgesteroneWhichRegimenAreYouUsing',
        next: {
          condition: {
            'hrt3MonthsFollowup_ifYouAreOnProgesteroneWhichRegimenAreYouUsing.cyclic':
              true
          },
          key: 'hrt3MonthsFollowup_areYouExperiencingVaginalBleeding1',
          els: 'hrt3MonthsFollowup_areYouExperiencingVaginalBleeding2'
        }
      },
      ,
      {
        key: 'hrt3MonthsFollowup_areYouExperiencingVaginalBleeding1',
        next: {
          key: 'hrt3MonthsFollowup_areYouExperiencingTheFollowingSymptoms'
        }
      },
      'hrt3MonthsFollowup_areYouExperiencingVaginalBleeding2',
      'hrt3MonthsFollowup_areYouExperiencingTheFollowingSymptoms',
      'hrt3MonthsFollowup_areYouExperiencingVaginalSymptoms',
      'hrt3MonthsFollowup_isIntercoursePainful',
      'hrt3MonthsFollowup_howIsYourMood',
      'hrt3MonthsFollowup_doYouExperienceHeadaches',
      'hrt3MonthsFollowup_doYouHaveHotFlashes',
      'hrt3MonthsFollowup_howIsYourSleep',
      'hrt3MonthsFollowup_hasThereBeenAnyChangesToYourHealthNewDiagnosisRecentHospitalVisitNewClotEvent',
      'hrt3MonthsFollowup_haveYouStartedAnyNewMedications'
    ]
  },

  allergies: {
    name: 'Allergies',
    questions: [
      'allergiesSymptoms',
      'allergiesLong',
      'allergiesDiff',
      'allergiesTriggers',
      'allergiesSurgeries',
      'allergiesSeasonal',
      'allergiesFamily',
      'allergiesTesting',
      'allergiesShots',
      'allergiesTreatments',
      'allergiesPrevTreatments',
      'allergiesConsume',
      'allergiesQuestions',
      'allergiesPreferredTreatment'
    ]
  },

  wlPhotos: {
    name: 'Weight Loss Photos',
    questions: ['wLPhotos']
  },

  tripleHair: {
    name: 'Triple Hair',
    questions: [
      'tripleHairPregnant',
      [
        {
          key: 'tripleHairSex',
          next: {
            condition: { 'tripleHairSex.male': true },
            key: 'tripleHairNorwood',
            els: 'tripleHairLudwig'
          }
        },
        {
          key: 'tripleHairNorwood',
          next: {
            condition: { 'tripleHairSex.male': false },
            key: 'tripleHairLudwig',
            els: 'tripleHairOver18'
          }
        },
        'tripleHairLudwig'
      ],
      'tripleHairOver18',
      'tripleHairAcknowledgement',
      'hairAcknowledgement2'
    ]
  },

  refill: {
    name: 'Agreed to Refill',
    questions: ['refillConsent']
  },
  norefill: {
    name: 'Disagreed to Refill',
    questions: ['noRefillConsent']
  },

  wl1stSession: {
    name: 'Weight Loss 1st Session',
    questions: ['wl1stSessionQ1', 'wl1stSessionPtFinalMsg']
  },

  wlPhysical: {
    name: 'Weight Loss Physical Activity',
    questions: ['wlPhysicalFirst']
  },

  WLPreSW: {
    name: 'Weight Loss Pre Social Worker',
    questions: [
      'WLPreSWMentalHealth',
      'WLPreSWSocialSupport',
      'WLPreSWDepressionAssessment',
      'WLPreSWAnxietyAssessment',
      'WLPreSWEmotionalEating',
      'WLPreSWGoals'
    ]
  },

  WLPreNP: {
    name: 'Weight Loss Pre Nurse Practitioner',
    questions: [
      'WLPreNPedAssessment',
      'WLPreNPemotionalEatingAssessment',
      'WLPreNPHistoryandPatterns'
    ]
  },

  wlPostLabCheck: {
    name: 'Weight Loss Post Lab Results',
    questions: ['WLPostLabPage1', 'WLPostLabPage2', 'WLPostAcknowledgement']
  },

  psycho: {
    name: 'Psychotherapist 1st Call',
    questions: ['psychoQuestions']
  },

  psychoFollowUp: {
    name: 'Psychotherapy follow up',
    questions: ['psychoFollowUp']
  },

  CS_VC_coordinatorsConsent: {
    name: 'Consent to Disclose Personal Health Info',
    questions: ['customerVirtuaCareCoordinators']
  },

  virtualCareConcerns: {
    name: 'General Team: Virtual Care Concerns',
    questions: ['virtualCareConcerns']
  },

  sharingInfoConsent: {
    name: 'Privacy and Sharing of Information Consent',
    questions: ['privacyAndSharingInformation']
  },

  //momwell forms
  epds: {
    name: 'EPDS',
    questions: [
      'epdsFunnySide',
      'epdsLookedEnjoyment',
      'epdsBlamedSelf',
      'epdsAniousGood',
      'epdsScaredGood',
      'epdsThingsGetting',
      'epdsDifficultySleeping',
      'epdsFeltSad',
      'epdsBeenCrying',
      'epdsHarmingSelf',
      'epdsEvaluated',
      'epdsEducationSlide'
    ]
  },

  phq9: {
    name: 'PHQ-9',
    questions: [
      'phqEducationSlide',
      'phqPleasure',
      'phqHopeLess',
      'phqTroubleFalling',
      'phqFeelingTired',
      'phqAppetite',
      'phqFeelingBad',
      'phqTroubleConcentrating',
      'phqSpeakingSlowly',
      'phqHurtingSelf',
      'phqEvaluated',
      'phqHowDifficult'
    ]
  },

  gad7: {
    name: 'GAD-7',
    questions: [
      'gadEducationSlide',
      'gadNervous',
      'gadControlWorying',
      'gadWorying',
      'gadTroubleRelaxing',
      'gadRestless',
      'gadIrritated',
      'gadFeelingAfraid',
      'gadEvaluated',
      'gadHowDifficult'
    ]
  },

  'bh-intake': {
    name: 'Intake Form',
    questions: [
      'intakeEducationSlide',
      'intakeFirst',
      'intakeEmergencyEvent',
      'intakeTherapyPart',
      'intakePersonalPart',
      'intakeFamilyPart',
      'intakeRelationshipPart',
      'intakePregnancyPart',
      'intakeLegalPart',
      // 'intakeConsentMomwellServices',
      // 'intakeTherapeuticProcess',
      // 'intakeClinicalNotes',
      // 'intakeSharingInformation',
      // 'intakeFees',
      // 'intakeGoodFaithEstimateForTherapy',
      // 'intakeCancellationPolicy',
      // 'intakeNotACrisisService',
      // 'intakeTermination',
      // 'intakeUseOfEmail',
      // 'intakeTechnologyRisks',
      'intakeHaveQuestions'
    ]
  },
  'bh-partner-intake': {
    name: 'Intake Partner Form',
    questions: [
      'intakeEducationSlide',
      'intakeFirst',
      'intakeEmergencyEvent',
      'intakeTherapyPartnerPart',
      'intakePersonalPart',
      'intakeFamilyPart',
      'intakeRelationshipPart',
      'intakePregnancyPart',
      'intakeLegalPart',
      // 'intakeConsentMomwellServices',
      // 'intakeTherapeuticProcess',
      // 'intakeClinicalNotes',
      // 'intakeSharingInformation',
      // 'intakeFees',
      // 'intakeGoodFaithEstimateForTherapy',
      // 'intakeCancellationPolicy',
      // 'intakeNotACrisisService',
      // 'intakeTermination',
      // 'intakeUseOfEmail',
      // 'intakeTechnologyRisks',
      'intakeHaveQuestions'
    ]
  },
  dass21: {
    name: 'DASS21',
    questions: [
      'dass21_intro',
      'dass21_1',
      'dass21_2',
      'dass21_3',
      'dass21_4',
      'dass21_5',
      'dass21_6',
      'dass21_7',
      'dass21_8',
      'dass21_9',
      'dass21_11',
      'dass21_12',
      'dass21_13',
      'dass21_14',
      'dass21_15',
      'dass21_16',
      'dass21_17',
      'dass21_18',
      'dass21_19',
      'dass21_20',
      'dass21_21'
    ]
  },
  pcl5: {
    name: 'PCL5',
    questions: [
      'pcl5_intro',
      'pcl5_1',
      'pcl5_2',
      'pcl5_3',
      'pcl5_4',
      'pcl5_5',
      'pcl5_6',
      'pcl5_7',
      'pcl5_8',
      'pcl5_9',
      'pcl5_11',
      'pcl5_12',
      'pcl5_13',
      'pcl5_14',
      'pcl5_15',
      'pcl5_16',
      'pcl5_17',
      'pcl5_18',
      'pcl5_19',
      'pcl5_20'
    ]
  },
  bms_before: {
    name: 'BMS before session',
    questions: [
      'bms_before_intro',
      'bms_depression',
      'bms_suicidal_urges',
      'bms_anxiety',
      'bms_anger',
      'bms_happiness',
      'bms_relationship',
      'bms_homework'
    ]
  },
  bms_after: {
    name: 'BMS after session',
    questions: [
      'bms_after_intro',
      'bms_depression',
      'bms_suicidal_urges',
      'bms_anxiety',
      'bms_anger',
      'bms_happiness',
      'bms_relationship',
      'bms_homework',
      'bms_after_1',
      'bms_after_2',
      'bms_after_3',
      'bms_after_4',
      'bms_after_5',
      'bms_after_6',
      'bms_after_7',
      'bms_after_8',
      'bms_after_9',
      'bms_after_10',
      'bms_after_11',
      'bms_after_12',
      'bms_after_13',
      'bms_after_14',
      'bms_after_15',
      'bms_after_16',
      'bms_after_17',
      'bms_after_18',
      'bms_after_19',
      'bms_after_20',
      'bms_after_least',
      'bms_after_best'
    ]
  },

  epipen: {
    name: 'Epipen',
    questions: [
      'epipenType',
      'epiPenweight',
      'epipenLocation',
      'epipenHowMany',
      'epipenMedicalReason',
      'epipenLastUsed',
      'epipenHowManyLocations',
      'epipenInsurance'
    ]
  },

  gn_welcome: {
    name: 'Onboarding',
    questions: [
      'gn_q_1',
      'gn_q_2',
      'gn_q_3',
      'gn_q_4',
      'gn_q_5',
      'gn_q_7',
      'gn_q_8',
      'gn_q_9',
      'gn_relationship',
      'gn_counselling_before',
      'gn_depression',
      'gn_medications',
      'gn_family_mental_history',
      'gn_q_journey_supported',
      'gn_q_anythingelse',
      'gn_q_hear'
    ],
    onSubmit: (answers: Answers, user: string) => {
      ConsultationUtils.answers = answers;
      window.location.href = 'https://goodnatal.com/join-request-confirmation';
    }
  },
  gn_intake: {
    name: 'Initial Intake',
    questions: [
      'gn_intake_0',
      'gn_intake_1',
      'gn_intake_2',
      'gn_intake_3',
      'gn_intake_4',
      'gn_intake_5',
      'gn_intake_6',
      'gn_intake_7',
      'gn_intake_8',
      'gn_intake_11',
      'gn_intake_12',
      'gn_intake_13',
      'gn_intake_14',
      'gn_intake_15',
      'gn_intake_16',
      'gn_intake_17',
      'gn_intake_18',
      'gn_intake_19',
      'gn_intake_20',
      'gn_intake_21',
      'gn_intake_23',
      'gn_intake_24',
      'gn_intake_25',
      'gn_intake_26',
      'gn_intake_27',
      'gn_intake_28',
      'gn_intake_29',
      'gn_intake_30',
      'gn_intake_31',
      'gn_intake_32',
      'gn_intake_33',
      'gn_intake_34',
      'gn_intake_35',
      'gn_intake_36',
      'gn_intake_37',
      'gn_intake_38',
      'gn_intake_39',
      'gn_intake_40',
      'gn_intake_41'
    ],
    onSubmit: (answers: Answers, user: string) => {
      ConsultationUtils.answers = answers;
      window.location.href = 'https://goodnatal.com/join-request-confirmation';
    }
  },
  endor_intake: {
    name: 'Endor Intake',
    questions: [
      'diabetesType',
      'insulinAdminister',
      'insulinPumpType',
      'pumpSupplies',
      'healthConditions',
      'medicalAllergies',
      'endorMedications',
      'endorSupplements',
      'currentGenderIdentity',
      'sexAtBirth'
    ]
  },
  gn_post_session: {
    name: 'Post Session Survey',
    questions: [
      'gn_post_1',
      'gn_post_2',
      'gn_post_3',
      'gn_post_4',
      'gn_post_5',
      'gn_post_6',
      'gn_post_7',
      'gn_post_8'
    ]
  },
  gn_blood_results: {
    name: 'Blood Results',
    questions: ['gn_blood_result']
  },
  consent: {
    name: 'Consent and Confidentiality',
    questions: [
      'firstsession_consent_intro',
      'firstsession_consent_overview',
      'firstsession_consent_session_confidentiality',
      'firstsession_consent_session_cancellations_and_missed_sessions',
      'firstsession_consent_consenting_age',
      'firstsession_consent_esign'
    ]
  },
  kinesiologist_checkin: {
    name: 'Physical Activity Goal Check Reflection',
    questions: [
      'biggestWinRecently',
      'changesNoticedSinceCreatingYourGoalWithKinesiologist',
      'whatDoYouFeelIsWorkingWellForYouRightNow',
      'whatChallengesExperiencedWhileWorkingOnAchievingYourGoal',
      [
        {
          key: 'minutesOfAerobicPhysicalActivityPerWeek',
          next: {
            condition: {
              'minutesOfAerobicPhysicalActivityPerWeek.minutesOfAerobicPhysicalActivityPerWeekDesc':
                { $gt: 0 }
            },
            key: 'feelingAfterDoingAerobicPhysicalActivity',
            els: 'daysPerWeekExerciseMuscleResistance'
          }
        },
        'feelingAfterDoingAerobicPhysicalActivity'
      ],
      'daysPerWeekExerciseMuscleResistance',
      'resistanceExerciseSessionLengthInMinuets'
    ]
  },
  rda25: {
    name: 'Registered Dietitian - 25 minutes',
    questions: ['rda25Province'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (
        answers.rda25Province.province == 'ON' ||
        answers.rda25Province.province == 'MB' ||
        answers.rda25Province.province == 'SK'
      ) {
        window.location.href =
          'https://calendly.com/bianca-cordeiro-rd/supplemental-dietitian-session-clone';
      } else {
        window.location.href =
          'https://calendly.com/bianca-cordeiro-rd/supplemental-dietitian-session-clone';
      }
    }
  },
  rda50: {
    name: 'Registered Dietitian - 50 minutes',
    questions: ['rda25Province'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (
        answers.rda25Province.province == 'ON' ||
        answers.rda25Province.province == 'MB' ||
        answers.rda25Province.province == 'SK'
      ) {
        window.location.href =
          'https://calendly.com/bianca-cordeiro-rd/supplemental-session';
      } else {
        window.location.href =
          'https://calendly.com/bianca-cordeiro-rd/supplemental-session?month=2023-10';
      }
    }
  },
  mha: {
    name: 'Mental Health - 55 minutes',
    questions: ['mhaProvince'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.mhaProvince.province == 'ON') {
        window.location.href =
          'https://calendly.com/radhika-psychotherapist/add-on-session';
      } else {
        window.location.href =
          'https://calendly.com/radhika-psychotherapist/add-on-session';
      }
    }
  },

  rka25: {
    name: 'Registered Kinesiologist - 25 minutes',
    questions: ['rka25Province'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.rka25Province.province == 'ON') {
        window.location.href =
          'https://calendly.com/dillan-rkin/supplementary-session-25';
      } else {
        window.location.href =
          'https://calendly.com/dillan-rkin/supplementary-session-25';
      }
    }
  },
  rka50: {
    name: 'Registered Kinesiologist - 50 minutes',
    questions: ['rka50Province'],
    onSubmit: (answers: Answers) => {
      debugger;
      ConsultationUtils.answers = answers;
      if (answers.rka50Province.province == 'ON') {
        window.location.href = window.location.href =
          'https://calendly.com/dillan-rkin/supplement';
      } else {
        window.location.href = 'https://calendly.com/dillan-rkin/supplement';
      }
    }
  },
  momwell_cali_consents: {
    name: 'Consent Forms',
    questions: [
      'momwell_cali_InformedConsentForTherapy',
      'momwell_cali_TelehealthConsent',
      'momwell_cali_BillingConsent',
      'momwell_cali_MarketingConsent',
      'momwell_cali_AckNOPP'
    ]
  },
  momwell_super_consents: {
    name: 'Consent Forms',
    questions: [
      'momwell_super_InformedConsentForTherapy',
      'momwell_super_TelehealthConsent',
      'momwell_super_BillingConsent',
      'momwell_super_MarketingConsent',
      'momwell_super_AckNOPP'
    ]
  },
  momwell_ny_consents: {
    name: 'Consent Forms',
    questions: [
      'momwell_ny_InformedConsentForTherapy',
      'momwell_ny_TelehealthConsent',
      'momwell_ny_BillingConsent',
      'momwell_ny_MarketingConsent',
      'momwell_ny_AckNOPP'
    ]
  },
  happyasamother_co_consents: {
    name: 'Consent Forms',
    questions: [
      'happyasamother_co_InformedConsentForTherapy',
      'happyasamother_co_TelehealthConsent',
      'happyasamother_co_BillingConsent',
      'happyasamother_co_MarketingConsent',
      'happyasamother_co_AckNOPP'
    ]
  },
  momwell_super_consents_v2: {
    name: 'Consent Forms',
    questions: [
      'momwell_super_InformedConsentForTherapy_v2',
      'momwell_super_TelehealthConsent_v2',
      'momwell_super_BillingConsent_v2',
      'momwell_super_MarketingConsent_v2',
      'momwell_super_AckNOPP_v2'
    ]
  },
  momwell_ny_consents_v2: {
    name: 'Consent Forms',
    questions: [
      'momwell_ny_InformedConsentForTherapy_v2',
      'momwell_ny_TelehealthConsent_v2',
      'momwell_ny_BillingConsent_v2',
      'momwell_ny_MarketingConsent_v2',
      'momwell_ny_AckNOPP_v2'
    ]
  },
  momwell_cali_consents_v2: {
    name: 'Consent Forms',
    questions: [
      'momwell_cali_InformedConsentForTherapy_v2',
      'momwell_cali_TelehealthConsent_v2',
      'momwell_cali_BillingConsent_v2',
      'momwell_cali_MarketingConsent_v2',
      'momwell_cali_AckNOPP_v2'
    ]
  },
  happyasamother_co_consents_v2: {
    name: 'Consent Forms',
    questions: [
      'happyasamother_co_InformedConsentForTherapy_v2',
      'happyasamother_co_TelehealthConsent_v2',
      'happyasamother_co_BillingConsent_v2',
      'happyasamother_co_MarketingConsent_v2',
      'happyasamother_co_AckNOPP_v2'
    ]
  },
  momwell_history_form: {
    name: 'Intake Form',
    questions: [
      'momwellHistoryFormIntro',
      'momwellHistoryFormContactInfo',
      'momwellHistoryFormEmergencyContactInformation',
      'momwellHistoryFormSocialRelationalInformation',
      'momwellHistoryFormMedicalMentalHealthHistory',
      'momwellHistoryFormReproductiveHealth',
      'momwellHistoryFormReasonForSeekingTherapy',
      'momwellHistoryFormSafetyAndLegalConsiderations'
    ]
  },
  momwell_partner_history_form: {
    name: 'Partner Intake Form',
    questions: [
      'momwellPartnerHistoryFormIntro',
      'momwellPartnerHistoryFormContactInfo',
      'momwellPartnerHistoryFormEmergencyContactInformation',
      'momwellPartnerHistoryFormSocialRelationalInformation',
      'momwellPartnerHistoryFormMedicalMentalHealthHistory',
      'momwellPartnerHistoryFormReproductiveHealth',
      'momwellPartnerHistoryFormReasonForSeekingTherapy',
      'momwellPartnerHistoryFormSafetyAndLegalConsiderations'
    ]
  },
  momwell_intake_network: {
    name: 'Momwell Intake (Network)',
    questions: [
      'momwellIntakeNetworkIntro',
      'momwellIntakeNetworkIntakeInformation',
      'momwellIntakeNetworkBasicRiskAssessment',
      'momwellIntakeNetworkBillingInformation',
      'momwellIntakeNetworkScheduling',
      'momwellIntakeNetworkNextSteps',
      'momwellIntakeNetworkFollowUp',
      'momwellIntakeNetworkNotes'
    ]
  },
  momwell_intake_cash: {
    name: 'Momwell Intake (Cash)',
    questions: [
      'momwellIntakeCashIntro',
      'momwellIntakeCashIntakeInformation',
      'momwellIntakeCashBasicRiskAssessment',
      'momwellIntakeCashBillingInformation',
      'momwellIntakeCashScheduling',
      'momwellIntakeCashNextSteps',
      'momwellIntakeCashFollowUp',
      'momwellIntakeCashNotes'
    ]
  },
  telehealth_consent: {
    name: 'Telehealth Consent',
    questions: ['telehealth_consent']
  },
  menopauseConsent: {
    name: 'Menopause Consent',
    questions: ['menopause_consent_form']
  },
  pocketpillsWaiver: {
    name: 'Consent for Multi-Month Refills',
    questions: ['pocketpills_waiver']
  },
  ssrs: {
    name: 'Columbia C SSRS Self Report',
    questions: [
      'ssr_wishDead',
      {
        key: 'ssr_thoughtsOfSuicide',
        next: {
          condition: {
            'ssr_thoughtsOfSuicide.ssr_thoughtsOfSuicide_yes': true
          },
          key: 'ssr_methodConsideration',
          els: 'ssr_suicideAttemptHistory'
        }
      },
      'ssr_methodConsideration',
      'ssr_suicidalIntent',
      'ssr_suicidePlanWithIntent',
      'ssr_suicideAttemptHistory',
      'ssr_recentAttempt'
    ]
  },
  momwell_super_consent_to_disclose: {
    name: 'Consent To Disclose',
    questions: [
      'momwell_super_consentToDisclose_info',
      'momwell_super_consentToDisclose_consent'
    ]
  },
  momwell_ny_consent_to_disclose: {
    name: 'Consent To Disclose',
    questions: [
      'momwell_ny_consentToDisclose_info',
      'momwell_ny_consentToDisclose_consent'
    ]
  },
  momwell_cali_consent_to_disclose: {
    name: 'Consent To Disclose',
    questions: [
      'momwell_cali_consentToDisclose_info',
      'momwell_cali_consentToDisclose_consent'
    ]
  },
  happyasamother_co_consent_to_disclose: {
    name: 'Consent To Disclose',
    questions: [
      'happyasamother_consentToDisclose_info',
      'happyasamother_consentToDisclose_consent'
    ]
  },
  enovone_testosterone_intake: {
    name: 'Testosterone Intake',
    questions: [
      'testosteroneIntake_decreaseInSexualDesire',
      'testosteroneIntake_reducedSexualReceptivity',
      'testosteroneIntake_triedTestosteroneTherapy',
      'testosteroneIntake_foundTherapyHelpful',
      'testosteroneIntake_estrogenAndrogenSensitiveCancer',
      'testosteroneIntake_pregnantTryingPregnantBreastfeeding',
      'testosteroneIntake_activeLiverDisease',
      'testosteroneIntake_severePsychiatricCondition',
      'testosteroneIntake_cardiovascularEvents',
      'testosteroneIntake_psychiatricHospitalizations',
      'testosteroneIntake_substanceUse',
      'testosteroneIntake_pcosElevatedAndrogen'
    ]
  }
};
